import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

export interface Form5291PDFData {
  // Designated Agent (pre-filled)
  agentName?: string;
  agentTitle?: string;
  agentAddress?: string;
  // Grantor / Importer of Record (exact 19 CFR 141.32 fields)
  importerName: string;
  importerEin?: string;
  importerPhone?: string;
  importerAddress?: string;
  entityType?: string;
  grantorDba?: string;
  grantorStateOfIncorp?: string;
  // Partnership — all general partners (19 CFR 141.39)
  partnerNames?: string[];
  // Limited Partnership — general partners with authority to bind
  limitedPartnerNames?: string[];
  // Witness clause fields
  cbpPort?: string;
  effectiveDay?: string;
  effectiveYear?: string;
  effectiveMonth?: string;
  witness1?: string;
  witness2?: string;
  // Signature (first officer)
  electronicSignature: string;
  signerTitle?: string;
  // Corporate Certification (second officer — corporations only)
  certOfficerName?: string;
  certOfficerCapacity?: string;
  certBoardDate?: string;
  certCity?: string;
  certState?: string;
  certSig?: string;
  // Meta
  clientName: string;
  clientEmail: string;
  submittedAt: string;
}

const GOLD = rgb(0.992, 0.714, 0.075);
const DARK = rgb(0.051, 0.082, 0.125);
const WHITE = rgb(1, 1, 1);
const GRAY = rgb(0.6, 0.6, 0.6);
const GREEN = rgb(0.2, 0.78, 0.35);
const BLACK = rgb(0, 0, 0);
const LIGHT_GRAY = rgb(0.95, 0.95, 0.95);

function drawRect(
  page: ReturnType<PDFDocument['addPage']>,
  x: number, y: number, w: number, h: number,
  color: ReturnType<typeof rgb>,
  opacity = 1,
) {
  page.drawRectangle({ x, y, width: w, height: h, color, opacity });
}

function drawText(
  page: ReturnType<PDFDocument['addPage']>,
  text: string,
  x: number, y: number,
  size: number,
  color: ReturnType<typeof rgb>,
  font: Awaited<ReturnType<PDFDocument['embedFont']>>,
) {
  if (!text) return;
  page.drawText(text, { x, y, size, color, font });
}

function drawLine(
  page: ReturnType<PDFDocument['addPage']>,
  x1: number, y1: number, x2: number, y2: number,
  color: ReturnType<typeof rgb>,
  thickness = 0.5,
) {
  page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness, color });
}

function truncate(str: string | undefined, maxLen: number): string {
  if (!str) return '';
  return str.length > maxLen ? `${str.slice(0, maxLen - 1)}\u2026` : str;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

export async function generateForm5291PDF(data: Form5291PDFData): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  const pageWidth = 612;
  const pageHeight = 792;
  const page = pdfDoc.addPage([pageWidth, pageHeight]);

  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const italicFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  const margin = 40;
  const contentWidth = pageWidth - margin * 2;

  // ── HEADER BANNER ──────────────────────────────────────────────────────────
  drawRect(page, 0, pageHeight - 80, pageWidth, 80, DARK);
  drawRect(page, 0, pageHeight - 82, pageWidth, 2, GOLD);

  drawText(page, 'DEPARTMENT OF HOMELAND SECURITY', margin, pageHeight - 22, 7, GOLD, boldFont);
  drawText(page, 'U.S. Customs and Border Protection', margin, pageHeight - 34, 9, WHITE, regularFont);
  drawText(page, 'POWER OF ATTORNEY / DESIGNATION OF CUSTOMS AGENT', margin, pageHeight - 52, 12, WHITE, boldFont);
  drawText(page, 'CBP Form 5291 \u2014 19 CFR 141.32 \u2014 Full Power and Authority including IEEPA Refunds / CAPE System', margin, pageHeight - 66, 7.5, GRAY, regularFont);

  drawText(page, 'CBP Form 5291', pageWidth - margin - 80, pageHeight - 22, 7, GRAY, regularFont);
  drawText(page, 'ESIGN Act Compliant', pageWidth - margin - 90, pageHeight - 34, 7, GOLD, boldFont);

  let y = pageHeight - 100;

  // ── SUBMISSION INFO BAR ────────────────────────────────────────────────────
  drawRect(page, margin, y - 22, contentWidth, 26, LIGHT_GRAY);
  drawText(page, `Submitted: ${formatDate(data.submittedAt)}`, margin + 8, y - 16, 8, BLACK, regularFont);
  drawText(page, `Client: ${truncate(data.clientName, 40)}`, margin + 180, y - 16, 8, BLACK, regularFont);
  drawText(page, `Email: ${truncate(data.clientEmail, 40)}`, margin + 360, y - 16, 8, BLACK, regularFont);
  y -= 38;

  // ── SECTION 1: DESIGNATED AGENT ───────────────────────────────────────────
  drawRect(page, margin, y - 18, contentWidth, 20, DARK);
  drawText(page, '  SECTION 1 \u2014 DESIGNATED AGENT  (Pre-Filled by Attorney)', margin, y - 13, 8.5, GOLD, boldFont);
  y -= 28;

  const agentName = data.agentName || 'Bruce Thabit, Esq.';
  const agentTitle = data.agentTitle || 'Director \u2014 Serenity Law PLLC';
  const agentAddr = data.agentAddress || '1037 NE 65th St #80300, Seattle, WA 98115';

  drawRect(page, margin, y - 46, contentWidth, 52, rgb(0.98, 0.97, 0.93));
  drawRect(page, margin, y - 46, 3, 52, GOLD);

  const col2 = contentWidth / 2;
  drawText(page, 'Agent Name:', margin + 8, y - 4, 7, GRAY, boldFont);
  drawText(page, agentName, margin + 8, y - 16, 10, BLACK, boldFont);

  drawText(page, 'Title:', margin + 8 + col2 / 2, y - 4, 7, GRAY, boldFont);
  drawText(page, agentTitle, margin + 8 + col2 / 2, y - 16, 9, BLACK, regularFont);

  drawText(page, 'Agent Address:', margin + 8, y - 28, 7, GRAY, boldFont);
  drawText(page, truncate(agentAddr, 60), margin + 8, y - 40, 8.5, BLACK, regularFont);
  y -= 60;

  drawLine(page, margin, y, margin + contentWidth, y, GRAY, 0.5);
  y -= 12;

  // ── SECTION 2: IMPORTER OF RECORD ─────────────────────────────────────────
  drawRect(page, margin, y - 18, contentWidth, 20, DARK);
  drawText(page, '  SECTION 2 \u2014 IMPORTER OF RECORD INFORMATION', margin, y - 13, 8.5, GOLD, boldFont);
  y -= 28;

  const entityLabel = data.entityType
    ? data.entityType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '';
  drawText(page, 'Entity Type:', margin, y, 7, GRAY, boldFont);
  drawText(page, entityLabel || 'N/A', margin + 70, y, 8, BLACK, boldFont);
  if (data.grantorStateOfIncorp) {
    drawText(page, 'State of Incorp.:', margin + 200, y, 7, GRAY, boldFont);
    drawText(page, data.grantorStateOfIncorp, margin + 280, y, 8, BLACK, regularFont);
  }
  y -= 16;

  drawText(page, 'Full Legal Name of Grantor (Importer of Record):', margin, y, 7.5, GRAY, boldFont);
  drawLine(page, margin, y - 3, margin + contentWidth, y - 3, GRAY, 0.3);
  drawText(page, truncate(data.importerName, 70), margin, y - 14, 9.5, BLACK, boldFont);
  y -= 28;

  if (data.grantorDba) {
    drawText(page, 'DBA:', margin, y, 7, GRAY, boldFont);
    drawText(page, truncate(data.grantorDba, 60), margin + 30, y, 8, BLACK, regularFont);
    y -= 16;
  }

  // Partnership — list all general partners
  if (data.entityType === 'partnership' && data.partnerNames && data.partnerNames.length > 0) {
    drawRect(page, margin, y - 14 - (data.partnerNames.length * 14), contentWidth, 18 + (data.partnerNames.length * 14), rgb(1, 0.98, 0.93));
    drawRect(page, margin, y - 14 - (data.partnerNames.length * 14), 3, 18 + (data.partnerNames.length * 14), rgb(0.85, 0.65, 0.1));
    drawText(page, 'General Partners (19 CFR 141.39):', margin + 8, y - 4, 7, GRAY, boldFont);
    data.partnerNames.forEach((name, idx) => {
      drawText(page, `${idx + 1}. ${truncate(name, 60)}`, margin + 8, y - 16 - (idx * 14), 8, BLACK, regularFont);
    });
    drawText(page, '* Partnership POA valid for 2 years maximum from execution date (19 CFR 141.39)', margin + 8, y - 16 - (data.partnerNames.length * 14) + 2, 6.5, rgb(0.7, 0.5, 0.1), italicFont);
    y -= 28 + (data.partnerNames.length * 14);
  }

  const col3 = contentWidth / 3;
  drawText(page, 'CBP / IRS Number (EIN):', margin, y, 7, GRAY, boldFont);
  drawText(page, truncate(data.importerEin || 'N/A', 25), margin, y - 12, 9, BLACK, regularFont);

  drawText(page, 'Telephone:', margin + col3, y, 7, GRAY, boldFont);
  drawText(page, truncate(data.importerPhone || 'N/A', 25), margin + col3, y - 12, 9, BLACK, regularFont);
  y -= 28;

  drawText(page, 'Importer Address:', margin, y, 7, GRAY, boldFont);
  drawText(page, truncate(data.importerAddress || '', 70), margin, y - 12, 9, BLACK, regularFont);
  y -= 28;

  drawLine(page, margin, y, margin + contentWidth, y, GRAY, 0.5);
  y -= 12;

  drawLine(page, margin, y, margin + contentWidth, y, GRAY, 0.5);
  y -= 12;

  // ── SECTION 3: ELECTRONIC SIGNATURE ───────────────────────────────────────
  drawRect(page, margin, y - 18, contentWidth, 20, DARK);
  drawText(page, '  SECTION 3 \u2014 ELECTRONIC SIGNATURE  (ESIGN Act, 15 U.S.C. \u00a7 7001)', margin, y - 13, 8.5, GOLD, boldFont);
  y -= 28;

  drawRect(page, margin, y - 60, contentWidth, 66, rgb(0.98, 0.97, 0.93));
  drawRect(page, margin, y - 60, 3, 66, GOLD);

  drawText(page, 'Electronic Signature (Typed Full Legal Name):', margin + 8, y - 6, 7.5, GRAY, boldFont);
  drawLine(page, margin + 8, y - 28, margin + contentWidth - 8, y - 28, BLACK, 0.8);
  drawText(page, truncate(data.electronicSignature, 55), margin + 8, y - 24, 14, BLACK, italicFont);

  if (data.signerTitle) {
    drawText(page, 'Title / Position:', margin + 8, y - 38, 7, GRAY, boldFont);
    drawText(page, truncate(data.signerTitle, 50), margin + 8, y - 50, 9, BLACK, regularFont);
  }

  drawText(page, `Date Signed: ${formatDate(data.submittedAt)}`, margin + col2, y - 38, 7, GRAY, boldFont);
  drawText(page, formatDate(data.submittedAt), margin + col2, y - 50, 9, BLACK, boldFont);
  y -= 76;

  // Certification text
  drawRect(page, margin, y - 36, contentWidth, 40, LIGHT_GRAY);
  drawText(page, 'CERTIFICATION: By signing above, the undersigned certifies that the information provided is true and accurate and designates', margin + 6, y - 10, 7, BLACK, regularFont);
  drawText(page, `${agentName} \u2014 ${agentTitle} as authorized customs agent with U.S. Customs and Border Protection.`, margin + 6, y - 20, 7, BLACK, regularFont);
  drawText(page, 'This constitutes a legally binding electronic signature under the ESIGN Act (15 U.S.C. \u00a7 7001) and UETA. False statements may result in penalties (18 U.S.C. \u00a7 1001).', margin + 6, y - 30, 6.5, BLACK, regularFont);
  y -= 48;

  // ── SECTION 4: CORPORATE CERTIFICATION (corporations only — optional) ─────
  if (data.entityType === 'corporation' && data.certOfficerName) {
    drawLine(page, margin, y, margin + contentWidth, y, GRAY, 0.5);
    y -= 12;
    drawRect(page, margin, y - 18, contentWidth, 20, DARK);
    drawText(page, '  SECTION 4 \u2014 CORPORATE CERTIFICATION  (Second Officer \u2014 Optional)', margin, y - 13, 8.5, GOLD, boldFont);
    y -= 28;

    drawRect(page, margin, y - 80, contentWidth, 86, rgb(0.97, 0.97, 0.99));
    drawRect(page, margin, y - 80, 3, 86, GOLD);

    const certLine1 = `I, ${truncate(data.certOfficerName || '', 30)}, certify that I am the ${truncate(data.certOfficerCapacity || '', 25)} of`;
    const certLine2 = `${truncate(data.importerName, 55)}, organized under the laws of the State of ${data.grantorStateOfIncorp || '___'};`;
    const certLine3 = `that ${truncate(data.electronicSignature, 30)} is the ${truncate(data.signerTitle || '', 25)} of said corporation,`;
    const certLine4 = `and that the Board of Directors adopted a resolution authorizing execution of powers of attorney`;
    const certLine5 = `at a meeting duly held on ${data.certBoardDate || '___'}.`;

    drawText(page, certLine1, margin + 8, y - 10, 8, BLACK, italicFont);
    drawText(page, certLine2, margin + 8, y - 22, 8, BLACK, italicFont);
    drawText(page, certLine3, margin + 8, y - 34, 8, BLACK, italicFont);
    drawText(page, certLine4, margin + 8, y - 46, 8, BLACK, italicFont);
    drawText(page, certLine5, margin + 8, y - 58, 8, BLACK, italicFont);

    drawText(page, 'Second Officer Signature:', margin + 8, y - 70, 7, GRAY, boldFont);
    drawLine(page, margin + 8, y - 78, margin + contentWidth / 2, y - 78, BLACK, 0.8);
    drawText(page, truncate(data.certSig || data.certOfficerName || '', 35), margin + 8, y - 76, 11, BLACK, italicFont);

    if (data.certCity || data.certState) {
      drawText(page, `City/State: ${data.certCity || ''}, ${data.certState || ''}`, margin + col2, y - 70, 7, GRAY, boldFont);
    }
    y -= 96;
  }

  // ── SECTION 5: PARTNERSHIP CERTIFICATE (partnerships only — optional) ─────
  if (data.entityType === 'partnership' && data.partnerNames && data.partnerNames.length > 0) {
    drawLine(page, margin, y, margin + contentWidth, y, GRAY, 0.5);
    y -= 12;
    drawRect(page, margin, y - 18, contentWidth, 20, DARK);
    drawText(page, '  SECTION 5 \u2014 PARTNERSHIP CERTIFICATE  (Not Required \u2014 Partnerships Only)', margin, y - 13, 8.5, GOLD, boldFont);
    y -= 28;

    const partnerBlockH = 20 + (data.partnerNames.length * 14) + 30;
    drawRect(page, margin, y - partnerBlockH, contentWidth, partnerBlockH + 6, rgb(1, 0.98, 0.93));
    drawRect(page, margin, y - partnerBlockH, 3, partnerBlockH + 6, rgb(0.85, 0.65, 0.1));

    drawText(page, 'Rule: A power of attorney granted by a partnership shall state the names of all members of the partnership.', margin + 8, y - 8, 7, rgb(0.6, 0.45, 0.05), italicFont);
    drawText(page, 'One member may execute a POA for all Customs business. A copy of the partnership agreement must accompany the POA.', margin + 8, y - 18, 7, rgb(0.6, 0.45, 0.05), italicFont);
    drawText(page, 'Names of All Members of the Partnership:', margin + 8, y - 32, 7.5, GRAY, boldFont);
    data.partnerNames.forEach((name, idx) => {
      drawText(page, `${idx + 1}. ${truncate(name, 65)}`, margin + 8, y - 44 - (idx * 14), 8.5, BLACK, regularFont);
    });
    y -= partnerBlockH + 16;
  }

  // ── SECTION 6: LIMITED PARTNERSHIP CERTIFICATE (limited partnerships — optional) ──
  if (data.entityType === 'limited_partnership' && data.limitedPartnerNames && data.limitedPartnerNames.length > 0) {
    drawLine(page, margin, y, margin + contentWidth, y, GRAY, 0.5);
    y -= 12;
    drawRect(page, margin, y - 18, contentWidth, 20, DARK);
    drawText(page, '  SECTION 6 \u2014 LIMITED PARTNERSHIP CERTIFICATE  (Not Required \u2014 Limited Partnerships Only)', margin, y - 13, 8.5, GOLD, boldFont);
    y -= 28;

    const lpBlockH = 20 + (data.limitedPartnerNames.length * 14) + 30;
    drawRect(page, margin, y - lpBlockH, contentWidth, lpBlockH + 6, rgb(1, 0.98, 0.93));
    drawRect(page, margin, y - lpBlockH, 3, lpBlockH + 6, rgb(0.85, 0.65, 0.1));

    drawText(page, 'Rule: A POA granted by a limited partnership need only state the names of the general partners who have authority to bind the firm', margin + 8, y - 8, 7, rgb(0.6, 0.45, 0.05), italicFont);
    drawText(page, 'unless the partnership agreement provides otherwise. A copy of the partnership agreement must accompany the POA.', margin + 8, y - 18, 7, rgb(0.6, 0.45, 0.05), italicFont);
    drawText(page, 'Names of General Partners with Authority to Bind the Firm:', margin + 8, y - 32, 7.5, GRAY, boldFont);
    data.limitedPartnerNames.forEach((name, idx) => {
      drawText(page, `${idx + 1}. ${truncate(name, 65)}`, margin + 8, y - 44 - (idx * 14), 8.5, BLACK, regularFont);
    });
    y -= lpBlockH + 16;
  }

  // ── AGENT ACCEPTANCE ───────────────────────────────────────────────────────
  drawRect(page, margin, y - 36, contentWidth, 40, rgb(0.93, 0.99, 0.95));
  drawRect(page, margin, y - 36, 3, 40, GREEN);

  drawText(page, 'AGENT ACCEPTANCE:', margin + 8, y - 10, 7.5, GRAY, boldFont);
  drawText(page, agentName, margin + 8, y - 22, 10, BLACK, boldFont);
  drawText(page, agentTitle, margin + 8, y - 34, 8, GRAY, regularFont);

  drawText(page, 'Accepted:', margin + col2, y - 10, 7, GRAY, boldFont);
  drawText(page, formatDate(data.submittedAt), margin + col2, y - 22, 9, BLACK, boldFont);
  y -= 48;

  // ── FOOTER ─────────────────────────────────────────────────────────────────
  drawLine(page, margin, 55, margin + contentWidth, 55, GOLD, 0.8);
  drawRect(page, 0, 0, pageWidth, 52, DARK);
  drawText(page, 'Serenity Law PLLC  |  Attorney Bruce Thabit, Esq.  |  1037 NE 65th St #80300, Seattle, WA 98115', margin, 34, 7, GRAY, regularFont);
  drawText(page, 'tariffrefund.attorney  |  CBP Form 5291 \u2014 Designation of Customs Agent', margin, 22, 7, GRAY, regularFont);
  drawText(page, `Generated: ${new Date().toLocaleString()}`, margin, 10, 6.5, rgb(0.4, 0.4, 0.4), regularFont);
  drawText(page, 'ESIGN Act Compliant Electronic Record', pageWidth - margin - 160, 10, 6.5, GOLD, boldFont);

  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
}

export function downloadPDF5291(bytes: Uint8Array, filename: string) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
